import React, { useCallback, useMemo } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../index';
import { EhsFormioReactComponent } from '../index';
import type { Component } from '@formio/react';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';
import { intl } from '../../../../lib';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import type {
  EhsFormioCheckboxGroupSettings,
  EhsFormioCheckboxGroupConstructor
} from './EhsFormioCheckboxGroupUtil';
import {
  findSelectOption,
  isArrayOfCheckboxOptions,
  isArrayOfStrings,
  transformDefaultValuesToCheckboxOptions,
  updateCheckboxGroupValue
} from './EhsFormioCheckboxGroupUtil';
import { CheckboxGroup, CheckboxGroupItem } from '../../../form';

export type CheckboxOption = {
  selectLabel: string;
  selectValue: string;
};

export interface ExtendedCheckboxOptions extends CheckboxOption {
  selected: boolean;
}

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export const EhsFormioCheckboxGroupType = 'ehsCheckboxGroup';

export class EhsFormioCheckboxGroup extends EhsFormioReactComponent<
  EhsFormioCheckboxGroupSettings,
  EhsFormioCheckboxGroupConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioCheckboxGroupType,
      label: 'common.unknown',
      key: EhsFormioCheckboxGroupType,
      classNames: '',
      ...extend
    });
  }

  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-CheckboxGroup',
      group: 'advanced',
      icon: 'check-square',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioCheckboxGroup.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            ' A string separated by spaces that will be added as classNames',
          weight: 20
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private selectOptions = [{ selectLabel: '', selectValue: '' }];
  private groupData: Array<CheckboxOption> = [];
  extractFields(component: Component<EhsFormioCheckboxGroupConstructor>): void {
    this.classNames = component.classNames;
    this.shouldSetValue = true;
    this.selectOptions = component.selectOptions;
    this.dataForSetting = this._data[component.key as string];
    this.groupData = isArrayOfCheckboxOptions(this.dataForSetting)
      ? this.dataForSetting
      : [];
  }

  attachEhsReact(): [
    (
      props: EhsFormioReactProps<EhsFormioCheckboxGroupSettings>
    ) => React.JSX.Element,
    EhsFormioCheckboxGroupSettings
  ] {
    return [
      CheckboxGroupCmp,
      {
        classNames: this.classNames,
        selectOptions: this.selectOptions,
        groupData: this.groupData
      }
    ];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param props formio {@link EhsFormioCheckboxGroupSettings} properties
 */
function CheckboxGroupCmp(
  props: EhsFormioReactProps<EhsFormioCheckboxGroupSettings>
): React.JSX.Element {
  const { onChange, defaultValue, settings } = props;

  const handleChange = useCallback(
    (newValue: CheckboxOption[]): void => {
      onChange(((newValue as unknown) as string) ?? '');
    },
    [onChange]
  );

  const translatedItems: ExtendedCheckboxOptions[] = useMemo(() => {
    return settings.selectOptions.map(option => {
      const isDefaultValueSelected =
        defaultValue?.includes(option.selectValue) ?? false;

      const isInPropsValue =
        isArrayOfCheckboxOptions(props.value) &&
        props.value.includes(option.selectValue);

      const isInGroupData =
        settings.groupData.some(
          groupData => groupData.selectValue === option.selectValue
        ) ?? false;

      return {
        selectValue: option.selectValue,
        selectLabel: intl(
          option.selectLabel as LanguageProperty,
          option.selectLabel
        ),
        selected: isDefaultValueSelected || isInPropsValue || isInGroupData
      };
    });
  }, [defaultValue, props.value, settings.groupData, settings.selectOptions]);

  return useMemo(
    () => (
      <FormioCheckboxGroup
        {...props}
        defaultValue={defaultValue}
        translatedItems={translatedItems}
        handleChange={handleChange}
      />
    ),
    [props, defaultValue, translatedItems, handleChange]
  );
}

type FormioCheckboxGroupProps = Pick<
  EhsFormioReactProps<EhsFormioCheckboxGroupSettings>,
  'settings' | 'label' | 'readOnly' | 'keyForComponent' | 'required'
> & {
  translatedItems: ExtendedCheckboxOptions[];
  value?: string | string[] | CheckboxOption[] | null;
  handleChange: (newValue: CheckboxOption[]) => void;
  defaultValue: string[];
};

function FormioCheckboxGroup({
  label,
  defaultValue,
  keyForComponent,
  readOnly,
  required,
  translatedItems,
  handleChange,
  value,
  settings
}: Readonly<FormioCheckboxGroupProps>): React.JSX.Element {
  const { errorMessage } = useQuestionnaireValidation(keyForComponent);

  const onChangeWrapper = useCallback(
    ({
      newValue,
      selectValue
    }: {
      newValue: boolean;
      selectValue: ExtendedCheckboxOptions['selectValue'];
    }): void => {
      const option = findSelectOption(selectValue, translatedItems);
      if (!option) {
        console.error('Option not found');
        return;
      }

      if (!value || value === '' || value.length === 0) {
        handleChange([option]);
        return;
      }

      if (isArrayOfStrings(value)) {
        const defaultCheckboxGroupValue = transformDefaultValuesToCheckboxOptions(
          defaultValue,
          translatedItems
        );
        if (newValue) {
          handleChange([...defaultCheckboxGroupValue, option]);
          return;
        }

        handleChange(
          updateCheckboxGroupValue(defaultCheckboxGroupValue, selectValue)
        );
      }

      if (isArrayOfCheckboxOptions(value)) {
        if (newValue) {
          handleChange([...value, option]);
          return;
        }
        handleChange(updateCheckboxGroupValue(value, selectValue));
      }
    },
    [defaultValue, handleChange, translatedItems, value]
  );

  return (
    <CheckboxGroup
      error={errorMessage}
      readOnly={readOnly}
      required={required}
      label={label}
      className={settings.classNames}
      layout="COLUMN"
    >
      {translatedItems.map(option => (
        <CheckboxGroupItem
          key={`${option.selectLabel}-${option.selectValue}`}
          label={option.selectLabel}
          value={option.selected}
          readOnly={readOnly}
          disabled={readOnly && !option.selected}
          defaultValue={false}
          onChange={newValue =>
            onChangeWrapper({
              selectValue: option.selectValue,
              newValue
            })
          }
        />
      ))}
    </CheckboxGroup>
  );
}
